import { Plus, Upload } from 'lucide-react'
import { useCallback, useState } from 'react'

import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Button from '@/elements/Button'
import Loading from '@/elements/Loading'
import Table from '@/components/Table'
import Modal from '@/components/Modal'
import Pagination from '@/components/Pagination'
import UploadModal from '@/components/UploadModal'
import useBreakpoints from '@/hooks/useBreakpoints'
import { useIndividualInnerCircleTable } from '@/hooks/tables/individualInnerCircleTable'
import useIndividualInnerCircleForm from '@/hooks/forms/useIndividualInnerCircleForm'
import type { UploadErrorsType } from '@/hooks/useUploadErrors'
import { useAssignments } from '@/hooks/useAssignments'
import type { SubjectResponse } from '@/api/types/Subject'
import type { IndividualAssignmentResponse } from '@/api/types/Organization'
import { uploadIndividualAssignmentsCSV } from '@/api/organizationIndividual'
import toast from '@/utils/toast'
import { INNER_CIRCLE_CSV_TEMPLATE } from '@/constants'

import IndividualInnerCircleForm from './IndividualInnerCircleForm'

function IndividualInnerCircle({ individualId }: { individualId: SubjectResponse['id'] }) {
  const [showAddModal, setShowAddModal] = useState(false)
  const { isTablet, isMobile } = useBreakpoints()
  const { form: innerCircleForm, resetForm } = useIndividualInnerCircleForm(null)

  const {
    assignments,
    pagination,
    isLoadingAssignments: isLoading,
    setPage,
    handleCreate,
    handleDelete,
    openDeleteModal,
    closeDeleteModal,
    showDeleteModal,
    refetch
  } = useAssignments({
    userId: individualId,
    type: 'INNER_CIRCLE',
    paginationKey: 'innerCircle',
    successMessages: {
      create: 'Inner circle member invited successfully!',
      delete: 'Inner circle member removed successfully'
    },
    errorMessages: {
      create: 'Failed to invite inner circle member',
      delete: 'Failed to remove inner circle member'
    }
  })

  const innerCircleTableData = useIndividualInnerCircleTable(
    assignments as IndividualAssignmentResponse[],
    openDeleteModal
  )

  const submitNewInnerCircle = useCallback(async () => {
    const isValid = await innerCircleForm.trigger()
    if (!isValid) return

    const values = innerCircleForm.getValues()
    const dirtyFields = innerCircleForm.formState.dirtyFields
    const formData = new FormData()

    Object.entries({
      ...(dirtyFields.avatar && { avatar: values.avatar || '' }),
      type: 'INNER_CIRCLE',
      firstName: values.firstName,
      lastName: values.lastName || '',
      email: values.email,
      city: values.city || '',
      state: values.state || '',
    }).forEach(([key, value]) => formData.append(key, value))

    const success = await handleCreate(formData, true)
    if (success) {
      setShowAddModal(false)
      resetForm(null)
    }
  }, [innerCircleForm, handleCreate, resetForm])

  const handleCloseAddModal = useCallback(() => {
    setShowAddModal(false)
    resetForm(null)
  }, [resetForm])

  const handleUploadInnerCircleCSV = useCallback(async (files: File[]) => {
    if (files.length === 0) return null

    const formData = new FormData()
    formData.append('csv', files[0])

    try {
      await uploadIndividualAssignmentsCSV(individualId, formData)
      toast.success('Inner Circle CSV uploaded successfully!')
      refetch()
      return null
    } catch (error: any) {
      const responseError = error.errors as UploadErrorsType
      toast.error(error.message || 'Failed to upload Inner Circle CSV.')
      return responseError
    }
  }, [individualId, refetch])


  return (
    <Flex wfull inline={!isTablet} gap={4} wrap>
      <Flex fraction={4} gap={2} wfull>
        <Text variant='h3'>Inner Circle</Text>
        <Text color='black-light-40'>
          Update user's inner circle.
        </Text>
      </Flex>
      <Flex fraction={6} wfull hfull align='flex-end' gap={6}>
        <Flex inline align='end' gap={3} gv={2} wfull={isTablet} wrap={isMobile}>
          <UploadModal
            title='Upload Inner Circle CSV'
            description='Select CSV file to upload'
            accept={{ 'text/csv': ['.csv'] }}
            onUpload={handleUploadInnerCircleCSV}
            sampleFileUrl={INNER_CIRCLE_CSV_TEMPLATE}
          >
            <Button fullWidth={isMobile}>
              <Upload size={18} style={{ minWidth: 18 }} />
              Upload CSV
            </Button>
          </UploadModal>
          <Button variant='primary' onClick={() => setShowAddModal(true)} fullWidth={isMobile}>
            <Plus size={18} style={{ minWidth: 18 }} />
            Invite
          </Button>
        </Flex>
        {isLoading ? (
          <Flex align='center' justify='center' wfull py={8}>
            <Loading />
          </Flex>
        ) : (
          <Flex wfull align='center' gap={3}>
            <Table {...innerCircleTableData} />
            <Modal
              isOpen={showDeleteModal}
              title='Remove Inner Circle'
              onSave={handleDelete}
              onCancel={closeDeleteModal}
              cancelLabel='No'
              saveLabel='Yes'
            >
              <Text color='black' style={{ hyphens: 'none' }}>
                Are you sure you want to remove this inner circle member? This action cannot be undone.
              </Text>
            </Modal>
            <Pagination
              variant={isMobile ? 'compact' : 'default'}
              onPageChange={setPage}
              {...pagination}
            />
          </Flex>
        )}
        <Modal
          title='Invite Inner Circle'
          isOpen={showAddModal}
          onSave={submitNewInnerCircle}
          onCancel={handleCloseAddModal}
          disabled={!innerCircleForm.formState.isDirty}
          style={{ width: 538 }}
          preventClickOutsideClose={innerCircleForm.formState.isDirty}
        >
          <IndividualInnerCircleForm {...innerCircleForm} />
        </Modal>
      </Flex>
    </Flex>
  )
}

export default IndividualInnerCircle
